/**
 * @format
 */
import { Component, DestroyRef, inject, Inject, OnDestroy, OnInit, Optional, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { StateService } from '@uirouter/angular';
import { ValidatablePlan } from 'common-typescript';
import { CourseUnit, OtmId, PersonWithModuleResponsibilityInfoType, StudyRight } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { BehaviorSubject, mergeMap, Subscription, take, tap } from 'rxjs';
import {
    AppErrorHandler,
    CourseUnitEntityService,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanActionsService,
    PlanEntityService,
    PlanManager,
    PlanOperation,
    sisuChain,
    UniversityService,
} from 'sis-components';
import { StudentPlanActionsService } from '../../../service/student-plan-actions.service';
import {
    RemoveSubstitutionsEvent,
    SelectSubstitutionsEvent,
} from '../course-unit-info-modal-substitutions/course-unit-info-modal-substitution-correspondences/course-unit-info-modal-substitution-correspondences.component';

export interface StudentCourseUnitInfoModalValues {
    courseUnit: CourseUnit;
    validatablePlan: ValidatablePlan;
    studyRight: StudyRight;
    allowEdit: boolean;
}

export function studentCourseUnitInfoModalOpener(): (
    modalValues: StudentCourseUnitInfoModalValues,
) => MatDialogRef<StudentCourseUnitInfoModalComponent, boolean> {
    const fudisDialogService = inject(FudisDialogService);
    return modalValues =>
        fudisDialogService.open(StudentCourseUnitInfoModalComponent, {
            data: modalValues,
        }) as MatDialogRef<StudentCourseUnitInfoModalComponent, boolean>;
}

@Component({
    selector: 'app-student-course-unit-info-modal',
    templateUrl: './student-course-unit-info-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        PlanManager,
        {
            provide: PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
            useClass: StudentPlanActionsService,
        },
    ],
    standalone: false,
})
export class StudentCourseUnitInfoModalComponent implements OnInit, OnDestroy {
    courseUnit: CourseUnit;
    currentCourseUnit: CourseUnit;
    validatablePlan: ValidatablePlan;
    studyRight: StudyRight;
    currentUniversityOrgId: OtmId;
    currentSavedValidatablePlan: ValidatablePlan;
    filteredResponsibilityInfos: PersonWithModuleResponsibilityInfoType[];
    planManagerSubscription: Subscription;
    planOperationSubscription: Subscription;
    allowEdit: boolean;
    selectedTab$ = new BehaviorSubject<string>('BASIC');
    versionChangeInProgress = false;

    private planManager: PlanManager = inject(PlanManager);

    private readonly courseUnitInfoModalOpener = studentCourseUnitInfoModalOpener();

    constructor(
        private planEntityService: PlanEntityService,
        private destroyRef: DestroyRef,
        private appErrorHandler: AppErrorHandler,
        @Optional() @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService,
        @Inject(MAT_DIALOG_DATA) public values: StudentCourseUnitInfoModalValues,
        public dialogRef: MatDialogRef<StudentCourseUnitInfoModalComponent, boolean>,
        private stateService: StateService,
        private universityService: UniversityService,
        private courseUnitEntityService: CourseUnitEntityService,
    ) {
        this.courseUnit = values.courseUnit;
        this.validatablePlan = values.validatablePlan;
        this.studyRight = values.studyRight;
        this.allowEdit = values.allowEdit;
    }

    close(stateChange: boolean = false) {
        this.dialogRef.close(stateChange);
    }

    ngOnInit() {
        this.currentUniversityOrgId = this.universityService.getCurrentUniversityOrgId();
        this.planManager.setValidatablePlan(_.cloneDeep(this.validatablePlan));
        this.createPlanOperationSubjectSubscription();
        this.planManagerSubscription = this.planManager.validatablePlanSubject.subscribe(newValidatablePlan => {
            this.validatablePlan = newValidatablePlan;
        });
        this.planOperationSubscription = this.planActionsService.planOperationSubject.subscribe((planOperation: PlanOperation) => {
            this.planManager.processPlanOperation(planOperation);
        });

        this.currentCourseUnit = _.cloneDeep(this.courseUnit);
        this.currentSavedValidatablePlan = _.cloneDeep(this.validatablePlan);
        this.filteredResponsibilityInfos = sisuChain(this.courseUnit.responsibilityInfos)
            .filter(
                (info: PersonWithModuleResponsibilityInfoType) =>
                    _.get(info, 'roleUrn') !== 'urn:code:module-responsibility-info-type:administrative-person',
            )
            .value();
    }

    ngOnDestroy() {
        this.planManagerSubscription.unsubscribe();
        this.planOperationSubscription.unsubscribe();
    }

    /**
     * Subscribes to planOperationSubject and processes the operations by passing them directly to planManager.
     */
    createPlanOperationSubjectSubscription(): void {
        this.planActionsService.planOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                mergeMap(operation => this.planManager.processPlanOperation(operation)),
            )
            .subscribe();
    }

    setModalCourseUnitVersion(courseUnit: CourseUnit) {
        this.planActionsService?.changeCourseUnitVersion(this.courseUnit, courseUnit);
        this.courseUnit = courseUnit;
        this.versionChangeInProgress = this.currentCourseUnit?.id !== courseUnit?.id;

        this.filteredResponsibilityInfos = sisuChain(this.courseUnit.responsibilityInfos)
            .filter(
                (info: PersonWithModuleResponsibilityInfoType) =>
                    _.get(info, 'roleUrn') !== 'urn:code:module-responsibility-info-type:administrative-person',
            )
            .value();
    }

    saveCourseUnitVersionChange() {
        this.planEntityService
            .updateMyPlan(this.planManager.validatablePlan.plan)
            .pipe(
                take(1),
                this.appErrorHandler.defaultErrorHandler(),
                tap(() => {
                    this.currentSavedValidatablePlan = this.planManager.validatablePlan;
                    this.currentCourseUnit = this.courseUnit;
                    this.versionChangeInProgress = false;
                }),
            )
            .subscribe();
    }

    openSubstitutionForm = (courseUnitId: OtmId, planId: OtmId) => {
        this.close(true);
        this.stateService.go('student.logged-in.profile.applications.create-prior-learning-substitution-application', {
            planId,
            courseUnitId,
        });
    };

    openApplication = (applicationId: OtmId) => {
        this.close(true);
        this.stateService.go('student.logged-in.profile.applications.prior-learning-application', { applicationId });
    };

    openCourseUnitInfoModal(courseUnitId: OtmId) {
        this.courseUnitEntityService
            .getById(courseUnitId)
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe((courseUnit: CourseUnit) => {
                this.courseUnitInfoModalOpener({
                    courseUnit,
                    validatablePlan: this.validatablePlan,
                    studyRight: this.studyRight,
                    allowEdit: this.allowEdit,
                });
            });
    }

    selectSubstitutions(event: SelectSubstitutionsEvent) {
        const { courseUnit, substitution, substituteCourseUnitsByGroupId } = event;
        this.planActionsService?.selectSubstitutions(courseUnit, substitution, substituteCourseUnitsByGroupId);
    }

    removeSubstitutions(event: RemoveSubstitutionsEvent) {
        const { courseUnit } = event;
        this.planActionsService?.removeSubstitutions(courseUnit);
    }
}
